import { unwrap } from "@sagot/api-client";
import { PRODUCT_NAME, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, Skeleton, type BadgeTone, type IconName } from "@sagot/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../../session/workspace-context";
import { useAgentList, useBillingOverview, type CustomDomainOut } from "../../shared";

const REMOVE_BRANDING = "remove_branding";

function Row({ title, description, status, action }: { title: string; description: ReactNode; status?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-t border-line py-3 first:border-t-0 first:pt-0">
      <div className="min-w-0 flex-1 basis-60">
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          {title}
          {status}
        </div>
        <p className="mt-0.5 text-xs text-fg-muted">{description}</p>
      </div>
      {action}
    </div>
  );
}

/** The chat opens at `https://<host>/` (mockup domains pin 4, BUG-SCOPE-004). */
function RootRow({ domain }: { domain: CustomDomainOut }) {
  const { t } = useTranslation();
  const url = `https://${domain.hostname}/`;
  return (
    <Row
      title={t("settings.domains.root.title")}
      status={
        <Badge tone="success">
          <Icon name="check-circle" className="size-3" />
          {t("settings.domains.root.on")}
        </Badge>
      }
      description={t("settings.domains.root.body", { url })}
      action={
        <Button asChild variant="secondary" size="sm">
          <a href={url} target="_blank" rel="noopener noreferrer" aria-label={t("settings.domains.root.openLabel", { host: domain.hostname })}>
            <Icon name="external-link" />
            {t("settings.domains.root.open")}
          </a>
        </Button>
      }
    />
  );
}

function useHostedBranding(agentId: string | undefined) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: ["ws", wsId, "domain-branding", agentId ?? ""],
    enabled: Boolean(agentId),
    retry: false,
    queryFn: async () => {
      const widget = await unwrap(api.GET("/v1/agents/{agent_id}/widget", { params: { path: { agent_id: agentId ?? "" } } }));
      const published = widget.published?.config.appearance as { show_branding?: unknown } | undefined;
      const appearance = published ?? widget.draft.appearance;
      return appearance?.show_branding !== false;
    },
  });
}

/**
 * "Powered by" on the hosted chat and sign-in pages (mockup domains pin 5, HCU-005). The switch
 * itself lives in Widget Studio › Appearance (one source for the widget and the web app); here the
 * state, the plan lock and the way to change it.
 */
function BrandingRow({ domain }: { domain: CustomDomainOut }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const billing = useBillingOverview();
  const agents = useAgentList();
  const agentId = domain.agent_id ?? agents.data?.[0]?.id;
  const branding = useHostedBranding(agentId);
  const allowed = billing.data?.features.includes(REMOVE_BRANDING) ?? false;
  const locked = billing.isSuccess && !allowed;
  const shown = branding.data !== false;
  let state: { tone: BadgeTone; icon: IconName; label: string } | null = null;
  if (locked) state = { tone: "warning", icon: "lock", label: t("settings.domains.branding.locked") };
  else if (branding.isSuccess) state = shown ? { tone: "neutral", icon: "eye", label: t("settings.domains.branding.shown") } : { tone: "success", icon: "eye-off", label: t("settings.domains.branding.hidden") };
  const title = t("settings.domains.branding.title", { product: PRODUCT_NAME });
  return (
    <Row
      title={title}
      status={billing.isPending || (allowed && branding.isPending && Boolean(agentId)) ? <Skeleton className="h-5 w-16" /> : state ? <Badge tone={state.tone}><Icon name={state.icon} className="size-3" />{state.label}</Badge> : null}
      description={locked ? t("settings.domains.branding.lockedBody") : t("settings.domains.branding.body")}
      action={
        locked ? (
          <Button asChild variant="secondary" size="sm">
            <AppLink href={consoleRoutes.billing(summary.slug)}>
              <Icon name="sparkles" />
              {t("settings.domains.branding.upgrade")}
            </AppLink>
          </Button>
        ) : agentId ? (
          <Button asChild variant="secondary" size="sm">
            <AppLink href={consoleRoutes.widgetStudio(summary.slug, agentId, "appearance")}>
              <Icon name="palette" />
              {t("settings.domains.branding.open")}
            </AppLink>
          </Button>
        ) : null
      }
    />
  );
}

/** Whether the bot check allows this address (`turnstile_state`): added, or the manual step. */
function BotCheckRow({ domain }: { domain: CustomDomainOut }) {
  const { t } = useTranslation();
  if (!domain.turnstile_state || domain.turnstile_state === "not_applicable") return null;
  const added = domain.turnstile_state === "added";
  return (
    <Row
      title={t("settings.domains.botCheck.title")}
      status={
        <Badge tone={added ? "success" : "warning"}>
          <Icon name={added ? "shield-check" : "alert-triangle"} className="size-3" />
          {added ? t("settings.domains.botCheck.added") : t("settings.domains.botCheck.manual")}
        </Badge>
      }
      description={added ? t("settings.domains.botCheck.addedBody", { host: domain.hostname }) : t("settings.domains.botCheck.manualBody", { host: domain.hostname })}
    />
  );
}

/** Settings of a live domain: chat at the root, white label, bot-check hostname. */
export function DomainLiveRows({ domain }: { domain: CustomDomainOut }) {
  return (
    <div data-testid="domain-live-rows">
      <RootRow domain={domain} />
      <BrandingRow domain={domain} />
      <BotCheckRow domain={domain} />
    </div>
  );
}
